import React, { useState, useEffect, useRef } from "react";
import { Trash2 } from "lucide-react";
import "../admin/AdminPartner.css";

const API_BASE = process.env.REACT_APP_API_URL+"/admin/images";

const ImageUploadManager = () => {
  const [uploadedImages, setUploadedImages] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const res = await fetch(API_BASE);
      const data = await res.json();

      // ✅ Fix: backend returns an array directly, not { images: [] }
      setUploadedImages(Array.isArray(data) ? data : []);
    } catch (err) {
      setError("Failed to fetch images");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (file) => {
    if (file && file.type.startsWith("image/")) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setError("");
    } else {
      setError("Please select a valid image file");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) handleFileSelect(file);
  };

  const saveImageToDatabase = async () => {
    if (!selectedFile) return;

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const res = await fetch(`${API_BASE}/upload`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error("Upload failed");
      const newImage = await res.json();

      setUploadedImages((prev) => [newImage, ...prev]);
      setSelectedFile(null);
      setPreviewUrl(null);
      setSuccess("Image uploaded successfully!");
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      setError("Failed to upload image");
    } finally {
      setLoading(false);
    }
  };

  const deleteImage = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
      setUploadedImages((prev) => prev.filter((img) => img._id !== id));
      setSuccess("Image deleted successfully!");
    } catch {
      setError("Failed to delete image");
    }
  };

  return (
    <div className="image-manager">
      <h2 className="section-title">Uploaded Images</h2>
      {loading && <p>Loading...</p>}
      <div className="image-grid">
        {uploadedImages.length > 0 ? (
          uploadedImages.map((image) => (
            <div key={image._id} className="image-item">
              <img
                src={process.env.REACT_APP_API_URL+`${image.url}`}
                alt={image.filename}
              />
              <button
                className="delete-overlay"
                onClick={() => deleteImage(image._id)}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))
        ) : (
          <p>No images uploaded yet.</p>
        )}
      </div>

      <h2 className="section-title">Upload New</h2>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
      />
      {selectedFile && (
        <div className="preview-container">
          <div className="preview-image">
            <img src={previewUrl} alt="preview" />
          </div>
          <button onClick={saveImageToDatabase} disabled={loading}>
            {loading ? "Uploading..." : "Save to DB"}
          </button>
        </div>
      )}
      {error && <p className="error-message">{error}</p>}
      {success && <p className="success-message">{success}</p>}
    </div>
  );
};

export default ImageUploadManager;
